import { type RecommendedAcco } from '@/scopes/products/domain/types';
import {
  tk,
  Typography,
  Rating,
  ScorePill,
  PriceBox,
  Icon,
  Image,
  LazyLoader,
  SpaceBetween,
  verticalGap,
  BulletList,
} from '@sunwebgroup/ui-components';
import { buildImgSrc } from '@/helpers/image-url';
import { Card } from '@/scopes/common/components/Card';
import { styled } from 'styled-components';
import { useIntl } from '@/intl';
import { AccoPriceDetails } from './PriceDetails';
import { useState, type MouseEvent } from 'react';
import { addDays } from 'date-fns';

export const AccoCardContainer = styled(Card)`
  position: relative;
  flex: 0 0 320px;
  min-width: 0;
  margin-bottom: ${tk('spacing_xs')};
  display: flex;
  flex-direction: column;
  text-decoration: none;
  @media ${tk('mq_tablet')} {
    flex: 0 0 350px;
  }
  &:hover {
    color: inherit;
  }
`;

const ImageContainer = styled.div`
  flex: 0 0 320px;
  max-height: 180px;
  @media ${tk('mq_tablet')} {
    max-height: unset;
  }
`;

const StyledImage = styled(Image.Root)`
  object-fit: cover;
  height: 100%;
  width: 100%;
  display: block;
`;

const LazyLoaderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
`;

export const CardMain = styled.div`
  padding: ${tk('spacing_lg')};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  ${verticalGap('spacing_xs')}
`;

const Location = styled(SpaceBetween)`
  color: ${tk('color_semantic_text_strong')};
`;

const ReviewsScore = styled(SpaceBetween)`
  color: ${tk('color_semantic_text_strong')};
`;

const IconButton = styled.button`
  color: ${tk('color_semantic_icon_strong')};
  position: relative;
`;

const Overview = styled.div`
  flex-grow: 1;
`;

const PriceBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const CardBody = styled.div`
  margin-bottom: ${tk('spacing_sm')};
`;

const TripDates = styled(Typography)`
  text-transform: capitalize;
`;

export function AccoCard({ acco }: { acco: RecommendedAcco }) {
  const intl = useIntl();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModalOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const noContentFallbackText = intl.formatMessage({ id: 'no_image' });

  // Function coming from react-pages/SearchPage/Results/ProductCard/ScoresRatings.tsx
  // TODO refactor and extract this function to a shared location
  const getUserRating = (userScore: number) => {
    if (userScore >= 8) {
      return intl.formatMessage({
        id: 'product_card_user_rating_fantastic',
      });
    } else if (userScore >= 6) {
      return intl.formatMessage({
        id: 'product_card_user_rating_good',
      });
    } else if (userScore >= 4) {
      return intl.formatMessage({
        id: 'product_card_user_rating_average',
      });
    } else if (userScore >= 2) {
      return intl.formatMessage({
        id: 'product_card_user_rating_reasonable',
      });
    }

    return intl.formatMessage({
      id: 'product_card_user_rating_unsatisfied',
    });
  };

  const tripDates = () => {
    if (!acco.price.departureDate || !acco.price.durationInDays) {
      return null;
    }

    const departureDate = new Date(acco.price.departureDate);

    const locale = 'nl-NL';
    const formatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    };

    return `${departureDate.toLocaleDateString(locale, formatOptions as Intl.DateTimeFormatOptions)} -
    ${addDays(departureDate, acco.price.durationInDays - 1).toLocaleDateString(
      locale,
      formatOptions as Intl.DateTimeFormatOptions
    )}`;
  };

  const numberOfPeople = () => {
    const participants = acco.price.participants[0];
    return participants.adults + participants.children.length + participants.babies.length;
  };

  return (
    <AccoCardContainer as="a" href={acco.url} aria-label={acco.name} target="_blank">
      <ImageContainer>
        <LazyLoader>
          {({ inView }) => (
            <LazyLoaderWrapper>
              <StyledImage
                defaultSource={{ src: buildImgSrc(acco.imageUrl, 480, 320), width: 480, height: 320 }}
                loaderSource={{ src: buildImgSrc(acco.imageUrl, 20) }}
                sources={[]}
                alternativeText={acco.name}
                startImmediateLoad={inView}
              >
                <Image.Error>
                  <Icon glyph="IconNoImage" size="lg" />
                  <Typography $variant="body-lg-bold">{noContentFallbackText}</Typography>
                </Image.Error>
              </StyledImage>
            </LazyLoaderWrapper>
          )}
        </LazyLoader>
      </ImageContainer>

      <CardMain>
        <CardHeader>
          <SpaceBetween direction="x" alignItems="center" spacing={tk('spacing_xs')} justifyContent="space-between">
            <Rating rating={acco.rating} />
            {acco.userRating > 0 && (
              <ReviewsScore direction="x" alignItems="center" spacing={tk('spacing_xs')}>
                <Typography $variant="body-sm" color="">
                  {getUserRating(acco.userRating)}
                </Typography>
                <ScorePill>{acco.userRating}</ScorePill>
              </ReviewsScore>
            )}
          </SpaceBetween>

          <Typography $variant="heading-md">{acco.name}</Typography>

          <Location direction="x" alignItems="center" spacing={tk('spacing_2xs')}>
            <Icon glyph="IconPin" size="sm" />
            <Typography $variant="body-xs">
              {acco.city}, {acco.region}, {acco.country}
            </Typography>
          </Location>
        </CardHeader>

        <CardBody>
          {acco.usps?.length && (
            <BulletList.Root $size="small">
              {acco.usps?.map((usp) => <BulletList.Item key={usp}>{usp}</BulletList.Item>)}
            </BulletList.Root>
          )}
        </CardBody>

        <SpaceBetween direction="x" alignItems="flex-end" spacing={tk('spacing_xs')} justifyContent="space-between">
          <Overview>
            <div>
              <Typography $variant="body-sm">
                {acco.price.mealplan?.name}, {numberOfPeople()} {intl.formatMessage({ id: 'person_abbreviation' })}
              </Typography>
            </div>
            <div>
              <TripDates $variant="body-sm-bold">{tripDates()}</TripDates>
            </div>
          </Overview>
          <PriceBoxWrapper>
            <PriceBox
              size="small"
              type="box"
              price={intl.formatTruncatedCurrency(acco.price.averagePrice)}
              priceBoxText={intl.formatMessage({ id: 'price_box_price_text' })} // TODO hardcoded text?
              priceDiscounts={
                acco.price.discount
                  ? {
                      discountPercentage: acco.price.discount.percentage,
                      originalPrice: intl.formatTruncatedCurrency(acco.price.discount.originalPrice),
                    }
                  : undefined
              }
            >
              <IconButton onClick={handleModalOpen}>
                <Icon glyph="IconInformationCircle" size="sm" />
              </IconButton>
              {isModalOpen ? (
                <AccoPriceDetails onClose={handleModalClose} price={acco.price} accommodationId={acco.id} />
              ) : null}
            </PriceBox>
          </PriceBoxWrapper>
        </SpaceBetween>
      </CardMain>
    </AccoCardContainer>
  );
}

AccoCard.displayName = 'AccoCard';
